import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { axiosClient } from 'api/apiClient'
import { MiniappEmbed, MiniappEmbedSchema, staleTime24Hours } from 'use-towns-client'
import { env } from 'utils'
import { z } from 'zod'

const miniappUnfurlSchema = z.object({
    url: z.string(),
    type: z.literal('miniapp'),
    title: z.string().optional(),
    description: z.string().optional(),
    image: z
        .object({
            url: z.string(),
            width: z.number().optional(),
            height: z.number().optional(),
        })
        .optional(),
    info: MiniappEmbedSchema,
})

const unfurlResponseSchema = z.object({
    data: z.array(z.union([miniappUnfurlSchema, z.any()])),
})

class MiniappEmbedError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'MiniappEmbedError'
    }
}

async function fetchMiniappEmbed(url: string, signal?: AbortSignal): Promise<MiniappEmbed> {
    const UNFURL_SERVER_URL = env.VITE_UNFURL_SERVER_URL

    if (!UNFURL_SERVER_URL) {
        console.error('[Miniapp] Unfurl server URL not configured')
        throw new MiniappEmbedError('Unfurl server URL not configured')
    }

    try {
        const encodedUrl = encodeURIComponent(url)
        const requestUrl = `${UNFURL_SERVER_URL}?url=${encodedUrl}`
        console.log('[Miniapp] Requesting unfurl from:', requestUrl)

        const response = await axiosClient.get(requestUrl, {
            signal,
        })

        console.log('[Miniapp] Received unfurl response:', response)

        const parsed = unfurlResponseSchema.safeParse(response)

        if (!parsed.success) {
            console.error('[Miniapp] Invalid unfurl response format:', parsed.error)
            throw new MiniappEmbedError('Invalid unfurl response format')
        }

        const unfurledData = parsed.data.data[0]
        if (!unfurledData) {
            console.error('[Miniapp] No unfurl data returned in response')
            throw new MiniappEmbedError('No unfurl data returned')
        }

        // Check if this is a miniapp unfurl
        const miniappParsed = miniappUnfurlSchema.safeParse(unfurledData)

        if (!miniappParsed.success) {
            console.log('[Miniapp] Unfurled data: ', url, miniappParsed.error)
            throw new MiniappEmbedError('URL does not contain miniapp metadata')
        }

        console.log('[Miniapp] Successfully parsed miniapp embed:', miniappParsed.data.info)
        return miniappParsed.data.info
    } catch (error) {
        console.error('[Miniapp] Error fetching miniapp embed for URL:', url, error)
        if (error instanceof MiniappEmbedError) {
            throw error
        }
        throw new MiniappEmbedError(
            `Failed to fetch miniapp embed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        )
    }
}

export const useMiniappEmbed = (url: string | null | undefined) => {
    const { data, status, error, isLoading, isFetching } = useQuery({
        queryKey: ['miniapp-embed', url],
        queryFn: ({ signal }) => {
            if (!url) {
                throw new MiniappEmbedError('Missing miniapp URL')
            }
            return fetchMiniappEmbed(url, signal)
        },
        staleTime: staleTime24Hours,
        retry: (failureCount, error) => {
            if (error instanceof MiniappEmbedError) {
                console.log('[Miniapp] Not retrying MiniappEmbedError:', error.message)
                return false
            }
            const shouldRetry = failureCount < 2
            console.log(`[Miniapp] Retry decision for attempt ${failureCount}:`, shouldRetry, error)
            return shouldRetry
        },
        enabled: !!url,
    })

    return useMemo(
        () => ({
            embed: data ?? null,
            status,
            error: error as Error | null,
            isLoading,
            isFetching,
        }),
        [data, error, isFetching, isLoading, status],
    )
}
